import type { ApplicationPublic, GrantPublic, UserPublic } from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, type ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { STR } from '../../i18n';
import {
  ApiError,
  createGrant,
  listApplications,
  listGrants,
  listUsers,
  reactivateUser,
  resetUserPassword,
  resetUserTwoFactor,
  revokeGrant,
  signOutEverywhere,
} from '../../lib/api';
import { noteSignedOut } from '../../lib/bounce';
import { formatDate, initials } from '../../lib/format';
import { queryKeys } from '../../lib/queryKeys';
import { useSession } from '../../lib/session';
import { LOGIN_PATH, surfaceById } from '../../lib/surfaces';
import { showToast } from '../../lib/toast';
import { twoStepStateOf } from '../../lib/twoStepState';
import { Modal, ModalParagraph, ModalProse } from '../../ui/Modal';
import {
  Callout,
  Card,
  CardHeader,
  Chip,
  Dot,
  EmptyRow,
  EmptyScreen,
  ListTruncationNotice,
  MasterDetailSkeleton,
  Tile,
} from '../../ui/Primitives';
import {
  buttonStyle,
  detailColumnStyle,
  detailNameStyle,
  detailPaneStyle,
  eyebrowStyle,
  grantRowStyle,
  listPaneHeaderStyle,
  listPaneStyle,
  listContainerStyle,
  listRowStyle,
  masterDetailStyle,
  modalButtonStyle,
  monoStyle,
  quietButtonStyle,
  rowActionStyle,
  searchFieldStyle,
} from '../../ui/styles';

import { hasItWork } from './access/itEmail/checklist';
import { ChecklistItEmailDialog } from './access/itEmail/ChecklistItEmailDialog';
import { useChecklistItEmail } from './access/itEmail/useChecklistItEmail';
import { PersonAccess } from './access/PersonAccess';
import { UsersSummary } from './access/UsersSummary';
import { AdminShell } from './AdminShell';
import { EditUserDialog } from './EditUserDialog';
import { GrantModal, RefusalModal, RevokeModal, TemporaryPasswordModal } from './SharedModals';
import { OnboardingDialog } from './onboarding/OnboardingDialog';
import { OffboardingChecklist } from './offboarding/OffboardingChecklist';
import { OffboardingDialog } from './offboarding/OffboardingDialog';

type Sort = 'name' | 'created' | 'status';

type Dialog =
  | { kind: 'create' }
  | { kind: 'edit'; user: UserPublic }
  | {
      kind: 'temporaryPassword';
      title: string;
      lead: string;
      password: string;
      name: string;
      /** Self-targeted reset: releasing this modal is what lets the wall fire. */
      refreshSessionOnClose?: boolean;
    }
  | { kind: 'offboard'; user: UserPublic }
  | { kind: 'refuseDeactivate'; name: string }
  | { kind: 'resetPassword'; user: UserPublic }
  | { kind: 'resetTwoFactor'; user: UserPublic }
  | { kind: 'signOutEverywhere'; user: UserPublic }
  | { kind: 'grant'; user: UserPublic }
  | { kind: 'revoke'; grant: GrantPublic };

const LIST_LIMIT = 200;

/** The Sessions row's count, styled as a link rather than the plain label it used to be. */
const sessionsCountLinkStyle: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 600,
  textDecoration: 'none',
  display: 'inline-block',
  padding: '2px 6px',
  margin: '-2px -6px',
  borderRadius: 6,
};

/**
 * The Users surface — variation C's master/detail. Every action here is an affordance over
 * a server decision: the last-active-admin refusals fire on the server's ACTUAL 409, never
 * on a client-side guess about who the last administrator is, because the client's snapshot
 * of that is always one write behind the truth.
 */
export function UsersPage() {
  const queryClient = useQueryClient();
  const session = useSession();
  /*
   * HR (SIR-7460 decision 1) reaches Users through the `user-manager` gate. AFFORDANCE ONLY:
   * for an HR actor this screen leaves out the reads and actions HR's session would be
   * refused (applications, grants, edit, password and two-step resets, reactivation,
   * deactivating an administrator). The server enforces every one of them independently.
   * Nothing changes for an administrator.
   */
  const hrOnly =
    session.data !== undefined &&
    !session.data.isAdmin &&
    (session.data.isHr || session.data.isIt);
  /**
   * The Summary pane (FINAL demo, spec row 4): Users OPENS on it. Picking a person shows
   * their page exactly as before; the Summary control above the list brings it back.
   */
  const [showSummary, setShowSummary] = useState(true);

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Sort>('name');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const offboardEmail = useChecklistItEmail();
  const [formError, setFormError] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: queryKeys.users,
    queryFn: () => listUsers({ limit: LIST_LIMIT }),
  });
  const applicationsQuery = useQuery({
    queryKey: queryKeys.applications,
    queryFn: () => listApplications({ limit: LIST_LIMIT }),
    enabled: !hrOnly,
  });

  const users = useMemo(() => usersQuery.data?.data ?? [], [usersQuery.data]);
  const applications = applicationsQuery.data?.data ?? [];

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    // C6 — title and department are searchable because they are directory labels, exactly
    // like the name. They are display-only: nothing here gates on them.
    const filtered =
      needle === ''
        ? users
        : users.filter((user) =>
            `${user.name} ${user.email} ${user.title ?? ''} ${user.department ?? ''}`
              .toLowerCase()
              .includes(needle),
          );
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      if (sort === 'created') return b.createdAt.localeCompare(a.createdAt);
      if (sort === 'status') {
        if (a.active !== b.active) return a.active ? -1 : 1;
        if (a.isAdmin !== b.isAdmin) return a.isAdmin ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
    return sorted;
  }, [users, search, sort]);

  const selected = useMemo(
    () => users.find((user) => user.id === selectedId) ?? visible[0] ?? null,
    [users, visible, selectedId],
  );

  const grantsQuery = useQuery({
    queryKey: queryKeys.grantsForUser(selected?.id ?? '', false),
    queryFn: () => listGrants({ userId: selected?.id ?? '', includeRevoked: false }),
    // Grants are admin-gated: HR never issues the read.
    enabled: selected !== null && !hrOnly,
  });
  const grants = grantsQuery.data?.data ?? [];

  /** The admin lists only — never `me`. Used where refreshing the session would unmount us. */
  function refreshListsOnly() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.users });
    void queryClient.invalidateQueries({ queryKey: queryKeys.allGrants });
  }

  function refresh() {
    refreshListsOnly();
    /*
     * `me` too: several of these actions can target the acting administrator. Demoting
     * yourself, or resetting your own password, changes the very facts the nav, the route
     * guard and the wall derive from — and without this the client keeps rendering a full
     * admin shell whose every read now 403s. The server is right either way; this keeps the
     * client's half of the three-layer law from going stale after the app's own write.
     */
    void queryClient.invalidateQueries({ queryKey: queryKeys.me });
  }

  function close() {
    // A deferred self-reset refresh lands HERE, once the one-time password has been seen.
    if (dialog?.kind === 'temporaryPassword' && dialog.refreshSessionOnClose === true) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
    }
    setDialog(null);
    setFormError(null);
  }

  /* ------------------------------------------------------------------ mutations -- */

  const reactivateMutation = useMutation({
    mutationFn: (user: UserPublic) => reactivateUser(user.id),
    onSuccess: (result) => {
      refresh();
      showToast(STR.toast.userReactivated(result.user.name));
    },
    onError: (error) => {
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  const resetMutation = useMutation({
    mutationFn: (user: UserPublic) => resetUserPassword(user.id),
    onSuccess: (result) => {
      const self = session.data?.id === result.user.id;
      /*
       * SHOW THE PASSWORD BEFORE MOVING THE PERSON. On a SELF-targeted reset the response
       * sets `must_change_password`, so refreshing `me` here makes the wall redirect fire
       * and unmount this page — taking the modal, and the one-time password inside it, with
       * it. That password exists in exactly one place: the response we are holding. It is
       * not logged, not audited, not stored in plaintext, and cannot be reissued. Throwing
       * it away locks the administrator out of their own account, recoverable only by an
       * operator on the droplet — the precise outcome the server's own cookie re-issue
       * exists to prevent.
       *
       * So on self, refresh the LISTS only and defer the `me` invalidation until the modal
       * is dismissed (see `close()`), at which point the wall fires as it should.
       */
      if (self) refreshListsOnly();
      else refresh();
      setDialog({
        kind: 'temporaryPassword',
        title: STR.modals.temporaryPassword.titleReset,
        lead: STR.modals.temporaryPassword.leadReset(result.user.name),
        password: result.temporaryPassword,
        name: result.user.name,
        // Dismissing this modal is what releases the wall.
        refreshSessionOnClose: self,
      });
      showToast(STR.toast.passwordReset(result.user.name));
    },
    onError: (error) => {
      close();
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  const resetTwoFactorMutation = useMutation({
    mutationFn: (user: UserPublic) => resetUserTwoFactor(user.id),
    onSuccess: (result) => {
      /*
       * A reset bumps the target's `session_version` — it ENDS their live sessions, the
       * acting administrator's own included on a self-target. Same dead-shell hazard as
       * sign-out-everywhere, same exit: leave via a full page load rather than staying on
       * an admin shell whose every request is about to 401. See `signOutMutation` below
       * for why noting-and-refreshing is not enough.
       */
      if (session.data?.id === result.user.id) {
        noteSignedOut();
        window.location.assign(LOGIN_PATH);
        return;
      }
      refresh();
      close();
      showToast(STR.toast.twoFactorReset(result.user.name), 'warn');
    },
    onError: (error) => {
      close();
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  const signOutMutation = useMutation({
    mutationFn: (user: UserPublic) => signOutEverywhere(user.id),
    onSuccess: (result) => {
      /*
       * Signing YOURSELF out everywhere kills the acting cookie too — so leave the same way
       * logout does, via a full page load. Merely noting it and refreshing is not enough:
       * `noteSignedOut()` clears the established-session flag, so the 401s that follow are
       * swallowed by `noteUnauthenticated()`'s early return and never reach the guard, while
       * TanStack Query keeps the last good `me` data through a failed refetch. The result is
       * a fully interactive admin shell on which every single request 401s until the page is
       * manually reloaded — exactly the dead-shell state the guard exists to prevent.
       */
      if (session.data?.id === result.user.id) {
        noteSignedOut();
        window.location.assign(LOGIN_PATH);
        return;
      }
      refresh();
      close();
      showToast(STR.toast.signedOutEverywhere(result.user.name), 'warn');
    },
    onError: (error) => {
      close();
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  const grantMutation = useMutation({
    mutationFn: ({ userId, applicationId }: { userId: string; applicationId: string }) =>
      createGrant({ userId, applicationId }),
    onSuccess: (result) => {
      refresh();
      close();
      showToast(STR.toast.accessGranted(result.grant.user.name, result.grant.application.name));
    },
    onError: (error) => {
      setFormError(error instanceof ApiError ? error.message : STR.errors.generic);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (grant: GrantPublic) => revokeGrant(grant.id),
    onSuccess: (result) => {
      refresh();
      close();
      showToast(
        STR.toast.accessRevoked(result.grant.user.name, result.grant.application.name),
        'warn',
      );
    },
    onError: (error) => {
      close();
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  /* --------------------------------------------------------------------- render -- */

  const loading = usersQuery.isLoading;
  const noUsers = !loading && users.length === 0;
  // `active` too, mirroring how the user pickers already filter: the server refuses a grant
  // into a deactivated application (400 `inactive_target`), so offering one is an affordance
  // promising something the decision layer will refuse.
  const grantableApplications = applications.filter(
    (application) =>
      application.active && !grants.some((grant) => grant.applicationId === application.id),
  );

  /*
   * D3 — THE HEADER DESCRIBES THE DIRECTORY, NOT THE PAGE. These counts come from
   * `meta.summary`, which the server computes in SQL over the whole users table: unfiltered,
   * uncapped, and deliberately unchanged by what is typed in the search box. Counting the
   * loaded array instead would read "200 active" for a 250-person directory.
   */
  const summary = usersQuery.data?.meta.summary ?? null;

  /*
   * …and the list itself is still capped at LIST_LIMIT, so the foot of it says so when it is
   * short. `meta.total` is the OTHER denominator — rows matching the current query, which
   * here is unfiltered, so it is the whole directory. Rows in hand versus that is the test,
   * and `ListTruncationNotice` owns it so the notice cannot appear on a complete list.
   */
  const total = usersQuery.data?.meta.total ?? null;

  return (
    <AdminShell
      surfaceId="users"
      {...(loading || noUsers
        ? {}
        : {
            primary: {
              label: STR.surfaces.users.primary,
              onClick: () => {
                setFormError(null);
                setDialog({ kind: 'create' });
              },
            },
          })}
    >
      {loading ? <MasterDetailSkeleton /> : null}

      {noUsers ? (
        <EmptyScreen
          title={STR.users.emptyTitle}
          body={STR.users.emptyBody}
          action={
            <button
              type="button"
              className="prim"
              style={{ ...modalButtonStyle }}
              onClick={() => {
                setFormError(null);
                setDialog({ kind: 'create' });
              }}
            >
              {STR.users.emptyAction}
            </button>
          }
        />
      ) : null}

      {!loading && !noUsers ? (
        <div style={masterDetailStyle}>
          <div style={listPaneStyle}>
            <div style={listPaneHeaderStyle}>
              {summary === null ? null : (
                <div className="tx-subtle" style={eyebrowStyle}>
                  {STR.users.meta(summary.active, summary.deactivated, summary.admins)}
                </div>
              )}
              <input
                className="field"
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
                placeholder={STR.users.searchPlaceholder}
                aria-label={STR.users.searchLabel}
                style={searchFieldStyle}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="tx-subtle" style={{ fontSize: 11, fontWeight: 650, flex: 'none' }}>
                  {STR.users.sortBy}
                </span>
                <select
                  className="field"
                  value={sort}
                  onChange={(event) => {
                    setSort(event.target.value as Sort);
                  }}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '5px 26px 5px 9px',
                    borderRadius: 7,
                    fontSize: 11.5,
                  }}
                >
                  <option value="name">{STR.users.sortName}</option>
                  <option value="created">{STR.users.sortCreated}</option>
                  <option value="status">{STR.users.sortStatus}</option>
                </select>
              </label>
              <button
                type="button"
                className="tbtn"
                aria-pressed={showSummary}
                onClick={() => {
                  setShowSummary(!showSummary);
                }}
                style={{ ...rowActionStyle, alignSelf: 'flex-start' }}
              >
                {STR.access.person.summaryButton}
              </button>
            </div>

            {visible.length === 0 ? (
              <div style={{ padding: '28px 14px', textAlign: 'center' }}>
                <div className="tx-subtle" style={{ fontSize: 12.5 }}>
                  {STR.users.noneMatch}
                </div>
                <button
                  type="button"
                  className="tbtn"
                  onClick={() => {
                    setSearch('');
                  }}
                  style={{ ...rowActionStyle, marginTop: 7 }}
                >
                  {STR.common.clearSearch}
                </button>
              </div>
            ) : null}

            <ul style={listContainerStyle}>
              {visible.map((user) => (
                <li key={user.id} {...(user.active ? {} : { 'data-off': '' })}>
                  <button
                    type="button"
                    className="li"
                    aria-current={!showSummary && selected?.id === user.id}
                    onClick={() => {
                      setSelectedId(user.id);
                      setShowSummary(false);
                    }}
                    style={listRowStyle}
                  >
                    <Dot tone={user.active ? 'ok' : 'neutral'} />
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span className="nm" style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>
                        {user.name}
                      </span>
                      <span
                        className="tx-subtle"
                        style={{
                          display: 'block',
                          fontSize: 11.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {user.email}
                      </span>
                    </span>
                    {user.mustChangePassword ? (
                      <Chip tone="attention" size="sm">
                        {STR.users.chipTemp}
                      </Chip>
                    ) : null}
                    {user.isAdmin ? (
                      <Chip tone="admin" size="sm">
                        {STR.users.chipAdmin}
                      </Chip>
                    ) : null}
                    {user.isHr ? (
                      <Chip tone="planned" size="sm">
                        {STR.access.person.chipHr}
                      </Chip>
                    ) : null}
                    {user.isIt ? (
                      <Chip tone="planned" size="sm">
                        {STR.access.person.chipIt}
                      </Chip>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>

            <ListTruncationNotice loaded={users.length} total={total} />
          </div>

          <div style={detailPaneStyle}>
            {selected === null || showSummary ? (
              <>
                {selected === null ? <p className="tx-subtle">{STR.users.selectPrompt}</p> : null}
                <UsersSummary
                  summary={summary}
                  users={users}
                  truncated={total !== null && users.length < total}
                  hrOnly={hrOnly}
                  onOpen={(userId) => {
                    setSelectedId(userId);
                    setSearch('');
                    setShowSummary(false);
                  }}
                />
              </>
            ) : (
              <UserDetail
                user={selected}
                hrOnly={hrOnly}
                access={
                  <>
                    <OffboardingChecklist key={`offboarding:${selected.id}`} user={selected} />
                    <PersonAccess key={selected.id} user={selected} hrOnly={hrOnly} />
                  </>
                }
                grants={grants}
                grantsLoading={hrOnly || grantsQuery.isLoading}
                canGrant={grantableApplications.length > 0}
                onEdit={() => {
                  setDialog({ kind: 'edit', user: selected });
                }}
                onReset={() => {
                  setDialog({ kind: 'resetPassword', user: selected });
                }}
                onToggleActive={() => {
                  if (selected.active) setDialog({ kind: 'offboard', user: selected });
                  else reactivateMutation.mutate(selected);
                }}
                onResetTwoFactor={() => {
                  setDialog({ kind: 'resetTwoFactor', user: selected });
                }}
                onSignOutEverywhere={() => {
                  setDialog({ kind: 'signOutEverywhere', user: selected });
                }}
                onGrant={() => {
                  setFormError(null);
                  setDialog({ kind: 'grant', user: selected });
                }}
                onRevoke={(grant) => {
                  setDialog({ kind: 'revoke', grant });
                }}
              />
            )}
          </div>
        </div>
      ) : null}

      {dialog?.kind === 'create' ? (
        <OnboardingDialog
          hrOnly={hrOnly}
          onCancel={close}
          onCreated={(result) => {
            setSelectedId(result.user.id);
            setShowSummary(false);
            setFormError(null);
            setDialog({
              kind: 'temporaryPassword',
              title: STR.modals.temporaryPassword.titleCreated,
              lead: STR.modals.temporaryPassword.leadCreated(result.user.name),
              password: result.temporaryPassword,
              name: result.user.name,
            });
            showToast(
              STR.access.onboarding.created(
                result.user.name,
                result.counts.givenNow,
                result.counts.requestsToAdmin,
              ),
            );
          }}
        />
      ) : null}

      {dialog?.kind === 'edit' ? <EditUserDialog user={dialog.user} onClose={close} /> : null}

      {dialog?.kind === 'temporaryPassword' ? (
        <TemporaryPasswordModal
          title={dialog.title}
          lead={dialog.lead}
          password={dialog.password}
          name={dialog.name}
          onClose={close}
        />
      ) : null}

      {dialog?.kind === 'offboard' ? (
        <OffboardingDialog
          user={dialog.user}
          onCancel={close}
          onOffboarded={(result) => {
            refresh();
            close();
            const deactivated = () => {
              showToast(STR.access.offboarding.completed(result.user.name), 'warn');
            };
            // FINAL demo: after Offboard, the email for IT opens at once when IT has work.
            if (!hasItWork(result.checklist)) {
              deactivated();
              return;
            }
            offboardEmail.draft.mutate(result.checklist, {
              onSuccess: () => {
                showToast(STR.itEmails.offboardedEmail(result.user.name), 'warn');
              },
              // The checklist card keeps Draft IT email, so a failed draft can be retried there.
              onError: deactivated,
            });
          }}
          onRefused={() => {
            setDialog({ kind: 'refuseDeactivate', name: dialog.user.name });
          }}
        />
      ) : null}

      {offboardEmail.open === null ? null : (
        <ChecklistItEmailDialog draft={offboardEmail.open} onClose={offboardEmail.close} />
      )}

      {dialog?.kind === 'refuseDeactivate' ? (
        <RefusalModal
          title={STR.modals.refuseDeactivate.title(dialog.name)}
          strong={STR.modals.refuseDeactivate.strong}
          body={STR.modals.refuseDeactivate.body}
          after={STR.modals.refuseDeactivate.after}
          onClose={close}
        />
      ) : null}

      {dialog?.kind === 'resetPassword' ? (
        <Modal
          title={STR.modals.resetPassword.title(dialog.user.name)}
          onClose={close}
          footer={
            <>
              <button type="button" className="ghost" style={modalButtonStyle} onClick={close}>
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="prim"
                style={modalButtonStyle}
                disabled={resetMutation.isPending}
                onClick={() => {
                  resetMutation.mutate(dialog.user);
                }}
              >
                {STR.modals.resetPassword.submit}
              </button>
            </>
          }
        >
          <ModalProse>
            <ModalParagraph>{STR.modals.resetPassword.body}</ModalParagraph>
          </ModalProse>
        </Modal>
      ) : null}

      {dialog?.kind === 'resetTwoFactor' ? (
        /*
         * The lost-phone path, per the design of record's "Reset two-step for …?" dialog.
         * Destructive treatment (dbtn) by operator ruling — this turns a protection OFF and
         * ends every live session — where the demo drew a primary button; the copy states
         * all three consequences in plain words because this dialog is the only place they
         * are stated. The server enforces independently and is idempotent; the button is
         * offered only for an enrolled account, which is affordance, not the gate.
         */
        <Modal
          title={STR.modals.resetTwoFactor.title(dialog.user.name)}
          onClose={close}
          footer={
            <>
              <button type="button" className="ghost" style={modalButtonStyle} onClick={close}>
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="dbtn"
                style={modalButtonStyle}
                disabled={resetTwoFactorMutation.isPending}
                onClick={() => {
                  resetTwoFactorMutation.mutate(dialog.user);
                }}
              >
                {STR.modals.resetTwoFactor.submit}
              </button>
            </>
          }
        >
          <ModalProse>
            <ModalParagraph>{STR.modals.resetTwoFactor.body}</ModalParagraph>
            <Callout tone="attention" style={{ marginTop: 12, fontWeight: 600 }}>
              {STR.modals.resetTwoFactor.warning}
            </Callout>
          </ModalProse>
        </Modal>
      ) : null}

      {dialog?.kind === 'signOutEverywhere' ? (
        <Modal
          title={STR.modals.signOutEverywhere.title(dialog.user.name)}
          onClose={close}
          footer={
            <>
              <button type="button" className="ghost" style={modalButtonStyle} onClick={close}>
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="dbtn"
                style={modalButtonStyle}
                disabled={signOutMutation.isPending}
                onClick={() => {
                  signOutMutation.mutate(dialog.user);
                }}
              >
                {STR.modals.signOutEverywhere.submit}
              </button>
            </>
          }
        >
          <ModalProse>
            <ModalParagraph>{STR.modals.signOutEverywhere.body}</ModalParagraph>
          </ModalProse>
        </Modal>
      ) : null}

      {dialog?.kind === 'grant' ? (
        <GrantModal
          context={STR.modals.grant.contextForPerson(dialog.user.name)}
          label={STR.modals.grant.pickApplication}
          options={grantableApplications.map((application: ApplicationPublic) => ({
            id: application.id,
            label: `${application.name} — ${application.baseUrl}`,
          }))}
          error={formError}
          busy={grantMutation.isPending}
          onCancel={close}
          onSubmit={(applicationId) => {
            if (applicationId === '') {
              setFormError(STR.modals.grant.required);
              return;
            }
            grantMutation.mutate({ userId: dialog.user.id, applicationId });
          }}
        />
      ) : null}

      {dialog?.kind === 'revoke' ? (
        <RevokeModal
          what={dialog.grant.application.name}
          who={dialog.grant.user.name}
          busy={revokeMutation.isPending}
          onCancel={close}
          onSubmit={() => {
            revokeMutation.mutate(dialog.grant);
          }}
        />
      ) : null}
    </AdminShell>
  );
}
/* ------------------------------------------------------------------- the detail -- */

/*
 * The web-layer derivation of the two-step state now lives in `../../lib/twoStepState`, so
 * the cross-layer parity test can import it without pulling this screen's React tree in.
 * Everything on the Security card — chip, note, and whether the Reset action is offered —
 * reads that one rule, so the three cannot disagree about which state a person is in.
 */

function UserDetail({
  user,
  hrOnly,
  access,
  grants,
  grantsLoading,
  canGrant,
  onEdit,
  onReset,
  onToggleActive,
  onResetTwoFactor,
  onSignOutEverywhere,
  onGrant,
  onRevoke,
}: {
  user: UserPublic;
  /** The actor is HR, not an administrator: admin-only actions are left out (affordance). */
  hrOnly: boolean;
  /** The added Access section (records, the glance grid, requests), drawn below the cards. */
  access: ReactNode;
  grants: GrantPublic[];
  grantsLoading: boolean;
  canGrant: boolean;
  onEdit: () => void;
  onReset: () => void;
  onToggleActive: () => void;
  onResetTwoFactor: () => void;
  onSignOutEverywhere: () => void;
  onGrant: () => void;
  onRevoke: (grant: GrantPublic) => void;
}) {
  // C6 — display-only directory metadata, joined the way the design of record joins it.
  const titleLine = [user.title, user.department].filter((part) => part !== null && part !== '');
  const twoStepState = twoStepStateOf(user);

  return (
    <div style={detailColumnStyle}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div
          className="sq"
          style={{
            width: 52,
            height: 52,
            borderRadius: 999,
            display: 'grid',
            placeItems: 'center',
            fontSize: 17,
            fontWeight: 700,
            flex: 'none',
          }}
          aria-hidden="true"
        >
          {initials(user.name)}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={detailNameStyle}>{user.name}</div>
          <div className="tx-muted" style={{ fontSize: 13 }}>
            {user.email}
          </div>
          {titleLine.length === 0 ? null : (
            <div className="tx-subtle" style={{ fontSize: 12.5, marginTop: 2 }}>
              {titleLine.join(' · ')}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 9 }}>
            <Chip tone={user.active ? 'positive' : 'neutral'}>
              {user.active ? STR.users.statusActive : STR.users.statusDeactivated}
            </Chip>
            <Chip tone={user.isAdmin ? 'admin' : 'neutral'}>
              {user.isAdmin ? STR.users.roleAdmin : STR.users.roleMember}
            </Chip>
            {user.isHr ? <Chip tone="planned">{STR.access.person.roleHr}</Chip> : null}
            {user.isIt ? <Chip tone="planned">{STR.access.person.roleIt}</Chip> : null}
            {user.mustChangePassword ? (
              <Chip tone="attention">{STR.users.mustChange}</Chip>
            ) : null}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {hrOnly ? null : (
          <>
          <button type="button" className="ghost" style={buttonStyle} onClick={onEdit}>
            {STR.users.edit}
          </button>
          <button type="button" className="ghost" style={buttonStyle} onClick={onReset}>
            {STR.users.resetPassword}
          </button>
          </>
          )}
          {/* HR: Deactivate a member only — never an administrator, never Reactivate. */}
          {hrOnly && user.active && user.isAdmin ? (
            <span className="tx-subtle" style={{ fontSize: 11.5, alignSelf: 'center' }}>
              {STR.access.person.hrCannotDeactivateAdmin}
            </span>
          ) : null}
          {hrOnly && !(user.active && !user.isAdmin) ? null : (
          <button
            type="button"
            className={user.active ? 'tbtn tbtn-danger' : 'tbtn'}
            style={{ ...buttonStyle, border: '1px solid var(--border-strong)' }}
            onClick={onToggleActive}
          >
            {user.active ? STR.users.deactivate : STR.users.reactivate}
          </button>
          )}
        </div>
      </div>

      {/*
        RULING 2 — there is no "last signed in" column in this system and nothing infers one,
        so the tile the demo shows for it is deliberately absent rather than faked.
      */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(168px,1fr))', gap: 12 }}>
        <Tile label={STR.users.createdLabel}>{formatDate(user.createdAt)}</Tile>
        <Tile label={STR.users.passwordLabel}>
          <span style={{ fontSize: 13.5 }}>
            {user.mustChangePassword ? STR.users.passwordTemporary : STR.users.passwordSettled}
          </span>
        </Tile>
        <Tile label={STR.users.applicationsLabel}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {grantsLoading ? STR.common.dash : grants.length}
          </span>
        </Tile>
      </div>

      <Card>
        <CardHeader title={STR.users.securityTitle} />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            padding: '16px 18px',
            borderBottom: '1px solid var(--border)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 180, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>{STR.users.twoStepTitle}</span>
              {/*
                The demo's three-state enum, rendered from the imported `twoStepStateOf` — the one
                derivation everything on this row shares. The amber chip is the demo's own
                `c-amber` treatment via the existing `attention` tone; off is an ordinary
                state, never a warning; and nothing here reads or infers the operator lock
                latch, which is deliberately absent from the shape.
              */}
              {twoStepState === 'on' ? (
                <Chip tone="positive">{STR.users.twoStepChipOn}</Chip>
              ) : twoStepState === 'reset' ? (
                <Chip tone="attention">{STR.users.twoStepChipReset}</Chip>
              ) : (
                <Chip tone="neutral">{STR.users.twoStepChipOff}</Chip>
              )}
            </div>
            <div
              className="tx-subtle"
              style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5, textWrap: 'pretty' }}
            >
              {twoStepState === 'on'
                ? user.twoFactorEnrolledAt === null
                  ? STR.users.twoStepNoteEnabled
                  : STR.users.twoStepNoteEnrolled(formatDate(user.twoFactorEnrolledAt))
                : twoStepState === 'reset'
                  ? STR.users.twoStepNoteReset(formatDate(user.twoFactorResetAt))
                  : STR.users.twoStepNoteOff}
            </div>
          </div>
          {/* Offered ONLY in the 'on' state, exactly as the demo draws it (its reset action
              is hidden for both other states) — in 'reset' and 'off' there is no second
              factor to reset, so offering the action would promise a no-op. Affordance,
              not the gate: the server enforces independently and the reset is idempotent. */}
          {twoStepState === 'on' && !hrOnly ? (
            <button type="button" className="ghost" style={buttonStyle} onClick={onResetTwoFactor}>
              {STR.users.resetTwoStep}
            </button>
          ) : null}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            padding: '16px 18px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 180, flex: 1 }}>
            {/*
              A live count now exists — `UserPublic.activeSessionCount`, derived from the
              sessions table's display-live rows (modules/sessions/service.ts). Ruling 1's "no
              count exists to show" is superseded now that a session store does exist; the
              label states it, correct singular/plural, and links to the Sessions screen via
              the surfaces table rather than a hardcoded path.
            */}
            {/* Sessions is an admin screen: for HR the count is plain text, not a link. */}
            {hrOnly ? (
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>
                {STR.users.sessionsCount(user.activeSessionCount)}
              </span>
            ) : (
            <Link
              to={surfaceById('sessions').path}
              className="tbtn"
              style={sessionsCountLinkStyle}
            >
              {STR.users.sessionsCount(user.activeSessionCount)}
            </Link>
            )}
            <div
              className="tx-subtle"
              style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5, textWrap: 'pretty' }}
            >
              {STR.users.sessionsNote}
            </div>
          </div>
          {hrOnly ? null : (
          <button type="button" className="ghost" style={buttonStyle} onClick={onSignOutEverywhere}>
            {STR.users.signOutEverywhere}
          </button>
          )}
        </div>
      </Card>

      {/* Application access reads grants, which are admin-gated: not drawn for HR. */}
      {hrOnly ? null : (
      <Card>
        <CardHeader
          title={STR.users.accessTitle}
          action={
            canGrant ? (
              <button type="button" className="tbtn" style={quietButtonStyle} onClick={onGrant}>
                {STR.users.grantApplication}
              </button>
            ) : undefined
          }
        />
        {!grantsLoading && grants.length === 0 ? <EmptyRow>{STR.users.noAccess}</EmptyRow> : null}
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {grants.map((grant) => (
            <li key={grant.id} className="row" style={grantRowStyle}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{grant.application.name}</div>
                <div className="tx-subtle" style={{ fontSize: 11.5, ...monoStyle }}>
                  {grant.application.key}
                </div>
              </div>
              <div className="tx-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                {STR.users.grantedLine(formatDate(grant.grantedAt))}
              </div>
              <button
                type="button"
                className="tbtn tbtn-danger"
                style={rowActionStyle}
                onClick={() => {
                  onRevoke(grant);
                }}
              >
                {STR.common.revoke}
              </button>
            </li>
          ))}
        </ul>
      </Card>
      )}

      {access}
    </div>
  );
}
